export interface IRuntimeConfig {
    globalConfig: IGlobalConfig;
    featuresConfig: Partial<typeof _featuresConfig>;
}

const RUNTIME_CONFIG_TIMEOUT_MS = 10000;
const FORWARDED_LAUNCH_PARAMS = ['partnerCode', 'apiUrl'];

const originOf = (url: string): string | null => {
    try {
        return new URL(url).origin;
    } catch {
        return null;
    }
};

export const requestRuntimeConfig = (): Promise<Response> => {
    const launchParams = new URLSearchParams(location.search);
    const params = new URLSearchParams();
    const referrerOrigin = originOf(document.referrer);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), RUNTIME_CONFIG_TIMEOUT_MS);

    for (const name of FORWARDED_LAUNCH_PARAMS) {
        for (const value of launchParams.getAll(name)) {
            params.append(name, value);
        }
    }

    if (referrerOrigin) {
        params.set('referrer', referrerOrigin);
    }

    return fetch(`/api/config?${params}`, { signal: controller.signal }).finally(() =>
        clearTimeout(timeout),
    );
};

export const loadRuntimeConfig = async (
    request: Promise<Response> = requestRuntimeConfig(),
): Promise<IRuntimeConfig> => {
    const response = await request;

    if (!response.ok) {
        throw new Error(`Runtime Config request failed with ${response.status}`);
    }

    return response.json();
};

const appendLoaderLogo = (logo: string): void => {
    const style = document.createElement('style');

    style.textContent = `
        body {
            margin: 0;
            background-color: #eee;
        }
        #_initialLoader {
            background: url('${logo}') no-repeat center;
            background-size: 40%;
            width: 100%;
            min-height: 500px;
            margin: 0 auto;
        }
        @media (min-width: 768px) {
            #_initialLoader {
                background-size: 15%;
            }
        }`;
    document.head.appendChild(style);
};

export const applyRuntimeConfig = ({ globalConfig, featuresConfig }: IRuntimeConfig): void => {
    Object.assign(window, { _globalConfig: globalConfig, _featuresConfig: featuresConfig });

    const darkLogo = featuresConfig?.brandedConfig?.logos?.dark;

    if (typeof darkLogo === 'string') {
        appendLoaderLogo(darkLogo);
    }
};

export const showBootError = (): void => {
    const loader = document.getElementById('_initialLoader');

    if (loader) {
        loader.textContent =
            'Something went wrong with iFrame front-end app, please contact Iframe-Webapp support';
    }
};
